import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { markRead, markAllRead, notificationHref, useNotificationSettings } from '../lib/notifications.js';
import { NotificationRow } from '../components/NotificationBell.jsx';

const FILTERS = [
  ['all', 'All'],
  ['new', 'Unread'],
  ['active', 'Active alerts'],
  ['ended', 'Resolved / expired'],
];

/** Notification settings: what notifies this account (in-app). */
export function NotificationSettings() {
  const { loading, error, options, settings, save, saving, saveError } = useNotificationSettings();
  // Deep link from the bell: /settings#notifications scrolls here once loaded.
  useEffect(() => {
    if (!loading && window.location.hash === '#notifications') document.getElementById('notifications')?.scrollIntoView({ block: 'start' });
  }, [loading]);
  if (loading) return <div className="loading">Loading settings…</div>;
  if (error) return <div className="banner err">Couldn’t load notification settings: {error}</div>;
  const toggle = (field, key) => {
    const list = settings[field].includes(key) ? settings[field].filter((x) => x !== key) : [...settings[field], key];
    save({ [field]: list });
  };
  return (
    <div className="form-card notif-settings" id="notifications">
      <h2>Notification settings</h2>
      <p className="muted">
        In-app notifications for alerts on your teams’ sites and routes. Each alert notifies once; it comes back as unread
        only if it escalates or returns after ending. Email, SMS and push aren’t available yet.
      </p>
      <fieldset className="form-fieldset">
        <legend>Assets</legend>
        <label className="check">
          <input type="checkbox" checked={settings.sites} onChange={(e) => save({ sites: e.target.checked })} />
          Sites
        </label>
        <label className="check">
          <input type="checkbox" checked={settings.routes} onChange={(e) => save({ routes: e.target.checked })} />
          Routes
        </label>
      </fieldset>
      <fieldset className="form-fieldset">
        <legend>Minimum severity</legend>
        {[
          [1, 'Everything (advisory / minor and up)'],
          [2, 'Warnings and up (moderate / warning / severe / critical)'],
          [3, 'Critical only (severe / extreme / critical)'],
        ].map(([v, label]) => (
          <label key={v} className="check">
            <input type="radio" name="minSeverity" checked={settings.minSeverity === v} onChange={() => save({ minSeverity: v })} />
            {label}
          </label>
        ))}
      </fieldset>
      <fieldset className="form-fieldset">
        <legend>Alert types</legend>
        {[
          ['official', 'Official alerts (NWS, ECCC, agency-reported events)'],
          ['threshold', 'App thresholds (calculated by HeatWatch from forecasts)'],
          ['regulatory', 'Regulatory (an enforceable work rule is met)'],
        ].map(([k, label]) => (
          <label key={k} className="check">
            <input type="checkbox" checked={settings.classes.includes(k)} onChange={() => toggle('classes', k)} />
            {label}
          </label>
        ))}
      </fieldset>
      <fieldset className="form-fieldset">
        <legend>Hazards</legend>
        <div className="form-checks">
          {Object.entries(options.hazards).map(([k, label]) => (
            <label key={k} className="check">
              <input type="checkbox" checked={settings.hazards.includes(k)} onChange={() => toggle('hazards', k)} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="muted" role="status">
        {saving ? 'Saving…' : saveError ? <span className="acct-err">Not saved: {saveError}</span> : 'Saved to your account.'}
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState(null);
  const [unread, setUnread] = useState(0);
  const [more, setMore] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (before) => {
    try {
      const r = await api(`/notifications?limit=30${before ? `&before=${encodeURIComponent(before)}` : ''}`);
      setItems((cur) => (before ? [...(cur ?? []), ...r.items] : r.items));
      setUnread(r.unread);
      setMore(r.items.length === 30);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);
  useEffect(() => {
    if (auth?.user) load();
  }, [auth?.user, load]);

  if (!auth || auth.loading) return <div className="app"><div className="loading">Loading…</div></div>;
  if (!auth.user) {
    return (
      <div className="app">
        <div className="banner">
          <Link to="/login">Sign in</Link> to see notifications for your sites and routes.
        </div>
      </div>
    );
  }

  const shown = (items ?? []).filter((n) =>
    filter === 'new' ? n.state === 'new' : filter === 'active' ? n.alertActive : filter === 'ended' ? !n.alertActive : true,
  );

  return (
    <div className="app notif-page">
      <div className="page-head">
        <Link to="/" className="btn-link">
          ← Map
        </Link>
        <h1>Notifications</h1>
      </div>
      <div className="controls">
        <div className="seg" role="tablist" aria-label="Filter notifications">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>
              {label}
            </button>
          ))}
        </div>
        <button
          className="btn"
          type="button"
          disabled={!unread}
          onClick={async () => {
            await markAllRead();
            load();
          }}
        >
          Mark all read
        </button>
      </div>
      {error && <div className="banner err">{error}</div>}
      {!items && !error && <div className="loading">Loading…</div>}
      {items && shown.length === 0 && <p className="muted">Nothing here.</p>}
      <div className="notif-list">
        {shown.map((n) => (
          <NotificationRow
            key={n.id}
            n={n}
            onOpen={async (x) => {
              if (!x.read) await markRead([x.id]).catch(() => {});
              navigate(notificationHref(x));
            }}
          />
        ))}
      </div>
      {more && (
        <button className="btn" type="button" onClick={() => load(items[items.length - 1].updatedAt)}>
          Load older
        </button>
      )}
      <p className="muted small-note">
        Official alerts come from NWS / ECCC and agency feeds. App thresholds and regulatory alerts are HeatWatch
        calculations from model forecasts and are never government warnings.
      </p>
      <NotificationSettings />
    </div>
  );
}
